import i18next from "i18next";
import PropTypes from "prop-types";
import { BiChevronDown } from "react-icons/bi";
import { CiCircleMinus, CiCirclePlus } from "react-icons/ci";
import { useDispatch, useSelector } from "react-redux";

import { FLIGHT_TYPE_CF, FLIGHT_TYPE_OW, FLIGHT_TYPE_RT } from "../../../constants";

import { useToggle } from "../../../hooks";

import { setCabin, setPassengers } from "../../../store/searchParamsSlice";

import { ReactComponent as AdultIcon } from "../../../assets/icons/adult.svg";
import { ReactComponent as ChildIcon } from "../../../assets/icons/child.svg";
import { ReactComponent as InfantIcon } from "../../../assets/icons/infant.svg";
import { ReactComponent as InfantWithPlaceIcon } from "../../../assets/icons/infantWithPlace.svg";

import { DropdownComponent } from "../../ui";

import {
  ClassBlock,
  ClassButton,
  ClassesBlock,
  CounterBlock,
  CounterButton,
  DropdownIcon,
  DropdownPassengerItem,
  DropdownPassengerItemTitleAndDescription,
  DropdownPassengersBlock,
  PassengerCount,
  PassengersCountBlock,
  Trigger,
  Wrapper,
} from "./style";

const SearchClassPassengersSelectComponent = ({ flightType }) => {
  const { t } = i18next;
  const {
    isTrue: isShowDropdown,
    set: { on, off },
  } = useToggle();

  const { cabin, passengers } = useSelector(state => state.searchParams);
  const dispatch = useDispatch();

  const cabins = [
    { cabin: "all", label: t("any_cabin") },
    { cabin: "economy", label: t("economy") },
    { cabin: "business", label: t("business") },
    { cabin: "first", label: t("first") },
  ];

  const handleMinusClick = type => {
    const currentPassengers = { ...passengers };

    if (type === "adt" && currentPassengers.adt > 1 && currentPassengers.adt > currentPassengers.inf) {
      currentPassengers.adt = currentPassengers.adt - 1;
      dispatch(setPassengers(currentPassengers));
    }

    if (type === "chd" && currentPassengers.chd > 0) {
      currentPassengers.chd = currentPassengers.chd - 1;
      dispatch(setPassengers(currentPassengers));
    }

    if (type === "ins" && currentPassengers.ins > 0) {
      currentPassengers.ins = currentPassengers.ins - 1;
      dispatch(setPassengers(currentPassengers));
    }

    if (type === "inf" && currentPassengers.inf > 0) {
      currentPassengers.inf = currentPassengers.inf - 1;
      dispatch(setPassengers(currentPassengers));
    }
  };

  const handlePlusClick = type => {
    const currentPassengers = { ...passengers };
    const passengersSum = currentPassengers.adt + currentPassengers.chd + currentPassengers.ins + currentPassengers.inf;

    if (passengersSum < 9) {
      if (type === "adt") {
        currentPassengers.adt = currentPassengers.adt + 1;
        dispatch(setPassengers(currentPassengers));
      }

      if (type === "chd") {
        currentPassengers.chd = currentPassengers.chd + 1;
        dispatch(setPassengers(currentPassengers));
      }

      if (type === "inf" && currentPassengers.inf < currentPassengers.adt) {
        currentPassengers.inf = currentPassengers.inf + 1;
        dispatch(setPassengers(currentPassengers));
      }

      if (type === "ins" && currentPassengers.ins <= 4) {
        currentPassengers.ins = currentPassengers.ins + 1;
        dispatch(setPassengers(currentPassengers));
      }
    }
  };

  const getIsDisabledMinus = type => {
    const currentPassengers = { ...passengers };

    if (type === "adt") {
      if (currentPassengers.adt === 1 || currentPassengers.adt <= currentPassengers.inf) {
        return true;
      }
    }

    if (type === "chd") {
      if (currentPassengers.chd === 0) {
        return true;
      }
    }

    if (type === "inf") {
      if (currentPassengers.inf === 0) {
        return true;
      }
    }

    if (type === "ins") {
      if (currentPassengers.ins === 0) {
        return true;
      }
    }
  };

  const getIsDisabledPlus = type => {
    const currentPassengers = { ...passengers };
    const passengersSum = currentPassengers.adt + currentPassengers.chd + currentPassengers.ins + currentPassengers.inf;

    if (passengersSum >= 9) {
      return true;
    }

    if (type === "ins" && passengers.ins >= 5) {
      return true;
    }

    if (type === "inf") {
      if (currentPassengers.inf >= currentPassengers.adt) {
        return true;
      }
    }
  };

  const handleClickCabin = newCabin => {
    dispatch(setCabin(newCabin));
  };

  return (
    <Wrapper flightType={flightType}>
      <Trigger type="button" onClick={on} aria-expanded={isShowDropdown} aria-haspopup="dialog">
        <ClassBlock>{cabins.find(value => value.cabin === cabin)?.label || t("any_cabin")}</ClassBlock>

        <PassengersCountBlock>
          <PassengerCount>
            <AdultIcon />
            <span>×{passengers.adt}</span>
          </PassengerCount>
          <PassengerCount>
            <ChildIcon />
            <span>×{passengers.chd}</span>
          </PassengerCount>
          <PassengerCount>
            <InfantIcon />
            <span>×{passengers.inf}</span>
          </PassengerCount>
          <PassengerCount>
            <InfantWithPlaceIcon />
            <span>×{+passengers.ins}</span>
          </PassengerCount>
        </PassengersCountBlock>

        <DropdownIcon className={isShowDropdown && "active"}>
          <BiChevronDown aria-hidden="true" />
        </DropdownIcon>
      </Trigger>

      {isShowDropdown && (
        <DropdownComponent show={isShowDropdown} onClickOutside={off} width={260} centered>
          <DropdownPassengersBlock>
            <DropdownPassengerItem>
              <AdultIcon />

              <DropdownPassengerItemTitleAndDescription>
                <span className="title">{t("adt_main_label")}</span>
                <span className="description">{t("adt_second_label")}</span>
              </DropdownPassengerItemTitleAndDescription>

              <CounterBlock>
                <CounterButton
                  type="button"
                  aria-label={t("pax_decrease", { category: t("adt_main_label") })}
                  disabled={Boolean(getIsDisabledMinus("adt"))}
                  onClick={() => handleMinusClick("adt")}
                >
                  <CiCircleMinus aria-hidden="true" />
                </CounterButton>
                <span aria-live="polite">{passengers.adt}</span>
                <CounterButton
                  type="button"
                  aria-label={t("pax_increase", { category: t("adt_main_label") })}
                  disabled={Boolean(getIsDisabledPlus("adt"))}
                  onClick={() => handlePlusClick("adt")}
                >
                  <CiCirclePlus aria-hidden="true" />
                </CounterButton>
              </CounterBlock>
            </DropdownPassengerItem>

            <DropdownPassengerItem>
              <ChildIcon />

              <DropdownPassengerItemTitleAndDescription>
                <span className="title">{t("chd_main_label")}</span>
                <span className="description">{t("chd_second_label")}</span>
              </DropdownPassengerItemTitleAndDescription>

              <CounterBlock>
                <CounterButton
                  type="button"
                  aria-label={t("pax_decrease", { category: t("chd_main_label") })}
                  disabled={Boolean(getIsDisabledMinus("chd"))}
                  onClick={() => handleMinusClick("chd")}
                >
                  <CiCircleMinus aria-hidden="true" />
                </CounterButton>
                <span aria-live="polite">{passengers.chd}</span>
                <CounterButton
                  type="button"
                  aria-label={t("pax_increase", { category: t("chd_main_label") })}
                  disabled={Boolean(getIsDisabledPlus("chd"))}
                  onClick={() => handlePlusClick("chd")}
                >
                  <CiCirclePlus aria-hidden="true" />
                </CounterButton>
              </CounterBlock>
            </DropdownPassengerItem>

            <DropdownPassengerItem>
              <InfantIcon />

              <DropdownPassengerItemTitleAndDescription>
                <span className="title">{t("inf_main_label")}</span>
                <span className="description">{t("inf_second_label")}</span>
              </DropdownPassengerItemTitleAndDescription>

              <CounterBlock>
                <CounterButton
                  type="button"
                  aria-label={t("pax_decrease", { category: t("inf_main_label") })}
                  disabled={Boolean(getIsDisabledMinus("inf"))}
                  onClick={() => handleMinusClick("inf")}
                >
                  <CiCircleMinus aria-hidden="true" />
                </CounterButton>
                <span aria-live="polite">{passengers.inf}</span>
                <CounterButton
                  type="button"
                  aria-label={t("pax_increase", { category: t("inf_main_label") })}
                  disabled={Boolean(getIsDisabledPlus("inf"))}
                  onClick={() => handlePlusClick("inf")}
                >
                  <CiCirclePlus aria-hidden="true" />
                </CounterButton>
              </CounterBlock>
            </DropdownPassengerItem>

            <DropdownPassengerItem>
              <InfantWithPlaceIcon />

              <DropdownPassengerItemTitleAndDescription>
                <span className="title">{t("inf_main_label")} </span>
                <span className="description">{t("ins_second_label")}</span>
              </DropdownPassengerItemTitleAndDescription>

              <CounterBlock>
                <CounterButton
                  type="button"
                  aria-label={t("pax_decrease", { category: t("inf_main_label") })}
                  disabled={Boolean(getIsDisabledMinus("ins"))}
                  onClick={() => handleMinusClick("ins")}
                >
                  <CiCircleMinus aria-hidden="true" />
                </CounterButton>
                <span aria-live="polite">{passengers.ins}</span>
                <CounterButton
                  type="button"
                  aria-label={t("pax_increase", { category: t("inf_main_label") })}
                  disabled={Boolean(getIsDisabledPlus("ins"))}
                  onClick={() => handlePlusClick("ins")}
                >
                  <CiCirclePlus aria-hidden="true" />
                </CounterButton>
              </CounterBlock>
            </DropdownPassengerItem>
          </DropdownPassengersBlock>

          <ClassesBlock>
            {cabins.map((data, index) => (
              <ClassButton
                key={data.cabin}
                onClick={() => handleClickCabin(data.cabin)}
                className={cabin === data.cabin && "active"}
                {...(index === 0 || index === 1 ? { position: "1/3" } : {})}
              >
                {data.label}
              </ClassButton>
            ))}
          </ClassesBlock>
        </DropdownComponent>
      )}
    </Wrapper>
  );
};

export default SearchClassPassengersSelectComponent;

SearchClassPassengersSelectComponent.defaultProps = {
  flightType: FLIGHT_TYPE_RT,
};

SearchClassPassengersSelectComponent.propTypes = {
  flightType: PropTypes.oneOf([FLIGHT_TYPE_OW, FLIGHT_TYPE_RT, FLIGHT_TYPE_CF]),
};
